import _ from 'lodash'

export const useTnaflix = () => {
	const {getFileNameFromUrl, extFetch} = useHelpers()

	/**
	 * Retry a function with exponential backoff
	 * @param {Function} fn - The function to retry
	 * @param {Object} options - Retry options
	 * @param {number} options.maxRetries - Maximum number of retries (default: 3)
	 * @param {number} options.initialDelay - Initial delay in ms (default: 1000)
	 * @param {number} options.maxDelay - Maximum delay in ms (default: 10000)
	 * @param {Function} options.shouldRetry - Function to determine if error is retryable
	 */
	async function retryWithBackoff(fn, options = {}) {
		const {
			maxRetries = 3,
			initialDelay = 1000,
			maxDelay = 10000,
			shouldRetry = (error) => {
				// Retry on network errors, timeout errors, or 5xx server errors
				if (!error) return false
				const errorMessage = (error.message || String(error)).toLowerCase()
				const retryableErrors = [
					'network error',
					'failed to fetch',
					'timeout',
					'connection refused',
					'connection reset',
					'econnrefused',
					'econnreset',
					'etimedout',
					'enetunreach',
					'aborted',
					'fetch failed',
					'error in extension',
					'chrome extension'
				]
				return retryableErrors.some(err => errorMessage.includes(err))
			}
		} = options

		let lastError
		for (let attempt = 0; attempt <= maxRetries; attempt++) {
			try {
				return await fn()
			} catch (error) {
				lastError = error
				const isLastAttempt = attempt === maxRetries
				const shouldRetryError = shouldRetry(error)

				if (isLastAttempt || !shouldRetryError) {
					throw error
				}

				// Calculate exponential backoff delay
				const delay = Math.min(initialDelay * Math.pow(2, attempt), maxDelay)
				console.log(`[useTnaflix] Retry attempt ${attempt + 1}/${maxRetries} after ${delay}ms. Error:`, error.message || error)
				await new Promise(resolve => setTimeout(resolve, delay))
			}
		}
		throw lastError
	}

	/**
	 * Extract video URL from tnaflix page
	 */
	async function getVideoFromUrl(url) {
		console.log('[useTnaflix] Processing URL:', url);
		try {
			// If it's already a direct video URL, return it
			if (url.match(/\.(mp4|webm|m3u8?)(\?|$)/i)) {
				let isHLS = !!url.match(/\.m3u8?(\?|$)/i)
				console.log('[useTnaflix] Direct URL detected. isHLS:', isHLS);
				let fileName = getFileNameFromUrl(url) || 'video.mp4'
				
				return {
					website: 'tnaflix.com',
					type: 'video',
					url: url,
					fileName: fileName,
					headers: {
						'Referer': 'https://www.tnaflix.com/',
						'Origin': 'https://www.tnaflix.com'
					},
					previewUrl: url,
					thumbnail: null,
					canView: true,
					hls: isHLS
				}
			}

			// Otherwise, try to fetch the page and extract video URL with retry
			try {
				let response = await retryWithBackoff(() => extFetch(url, {
					method: 'GET',
					headers: {
						'User-Agent': 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/143.0.0.0 Safari/537.36',
						'Referer': 'https://www.tnaflix.com/'
					}
				}), {
					maxRetries: 3,
					initialDelay: 1000,
					maxDelay: 8000
				})

				if (response && response.body) {
					let html = typeof response.body === 'string' ? response.body : (response.body.toString ? response.body.toString() : String(response.body))
					
					// Extract video sources
					const sources = [];
					const sourceRegex = /<source\s+src="([^"]+)"\s+type="video\/mp4"\s+size="(\d+)">/g;
					let match;
					while ((match = sourceRegex.exec(html)) !== null) {
						sources.push({
							url: match[1].replace(/&amp;/g, '&'),
							quality: parseInt(match[2])
						});
					}

					console.log('[useTnaflix] Found sources:', sources.length);

					if (sources && sources.length > 0) {
						// Sort by quality descending
						sources.sort((a, b) => b.quality - a.quality);
						const bestSource = sources[0];
						
						console.log(`[useTnaflix] Selected source: ${bestSource.quality}p`);

						const videoIdMatch = url.match(/video(\d+)/);
						const videoId = videoIdMatch ? videoIdMatch[1] : 'video';
						const fileName = `${videoId}_${bestSource.quality}p.mp4`;
						
						return {
							website: 'tnaflix.com',
							type: 'video',
							url: bestSource.url,
							fileName: fileName,
							headers: {
								'Referer': url,
								'Origin': 'https://www.tnaflix.com'
							},
							previewUrl: bestSource.url,
							thumbnail: null,
							canView: true,
							quality: bestSource.quality + 'p',
							hls: false
						}
					}
				}
			} catch (fetchError) {
				console.warn('[useTnaflix] Could not fetch page to extract video URL after retries:', fetchError)
			}

			// Fallback
			return {
				website: 'tnaflix.com',
				type: 'video',
				url: url,
				fileName: 'video.mp4',
				previewUrl: url,
				thumbnail: null,
				canView: true
			}
		} catch (error) {
			console.error('Error fetching video from URL:', error)
			return {
				website: 'tnaflix.com',
				type: 'video',
				url: url,
				fileName: 'video.mp4',
				previewUrl: url,
				thumbnail: null,
				canView: true
			}
		}
	}

	/**
	 * Process multiple URLs and extract videos
	 */
	async function getVideosFromUrls(urls) {
		let validUrls = urls
			.split('\n')
			.map(url => url.trim())
			.filter(url => url && url.length > 0)

		const CONCURRENCY = 5
		const results = new Array(validUrls.length)
		let nextIndex = 0

		const worker = async () => {
			while (nextIndex < validUrls.length) {
				const idx = nextIndex++
				results[idx] = await getVideoFromUrl(validUrls[idx])
			}
		}

		await Promise.all(
			Array.from({length: Math.min(CONCURRENCY, validUrls.length)}, () => worker())
		)

		return results.map((media, index) => ({
			...media,
			id: `tnaflix-${Date.now()}-${index}`,
			source: 'tnaflix'
		}))
	}

	return {
		getMedia: async (form) => {
			if (form.urls) return getVideosFromUrls(form.urls)
			if (form.url) {
				let media = await getVideoFromUrl(form.url)
				media.id = `tnaflix-${Date.now()}`
				media.source = 'tnaflix'
				return [media]
			}
			return []
		},
		getVideoFromUrl,
		getVideosFromUrls
	}
}
